import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchBarristerFreeBusy, type RawScheduleEvent } from './calendarApi';

export type { RawScheduleEvent };

export type ConflictSeverity = 'blocking' | 'warning';
export type ConflictKind = 'overlap' | 'adjacent';

export interface ConflictInfo {
  event: RawScheduleEvent;
  severity: ConflictSeverity;
  /** 'overlap' = times clash; 'adjacent' = too little gap before/after (tight turnaround). */
  kind: ConflictKind;
  /** For 'adjacent': minutes of gap between the two events. */
  gapMinutes?: number;
}

// Minimum gap a barrister wants between two commitments (no route calc yet — just a buffer).
const BUFFER_MINUTES = 30;

function eventsOverlap(
  aStart: Date, aEnd: Date,
  bStart: Date, bEnd: Date,
): boolean {
  return aStart < bEnd && aEnd > bStart;
}

function localDateStr(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// BLOCKING types are court-mandated commitments the barrister cannot reschedule.
const BLOCKING_TYPES = new Set(['hearing']);

/**
 * Pure conflict engine — "is the barrister already committed in this window?" Given their busy
 * events and a proposed start+duration, return every clash (overlap) and tight turnaround
 * (adjacent, under the buffer). Exported so the exact clash rule is unit-testable independent of
 * React Query / the hook. Compares absolute instants, so it is timezone-safe as long as both the
 * events and the proposed start use the same convention (both wall-clock-as-Z).
 */
export function detectConflicts(
  events: RawScheduleEvent[],
  startIso: string,
  durationMinutes: number,
  bufferMinutes: number = BUFFER_MINUTES,
): ConflictInfo[] {
  if (!startIso || durationMinutes <= 0) return [];
  const proposedStart = new Date(startIso);
  const proposedEnd = new Date(proposedStart.getTime() + durationMinutes * 60_000);
  const out: ConflictInfo[] = [];

  for (const e of events) {
    const eStart = new Date(e.startAt);
    const eEnd = new Date(e.endAt);

    // 1. Direct time overlap.
    if (eventsOverlap(proposedStart, proposedEnd, eStart, eEnd)) {
      out.push({ event: e, severity: BLOCKING_TYPES.has(e.eventType) ? 'blocking' : 'warning', kind: 'overlap' });
      continue;
    }

    // 2. Adjacent — no overlap, but the gap before/after is under the buffer.
    let gapMs: number | null = null;
    if (eEnd <= proposedStart) gapMs = proposedStart.getTime() - eEnd.getTime();       // event ends just before
    else if (eStart >= proposedEnd) gapMs = eStart.getTime() - proposedEnd.getTime();  // event starts just after
    if (gapMs !== null && gapMs < bufferMinutes * 60_000) {
      out.push({ event: e, severity: 'warning', kind: 'adjacent', gapMinutes: Math.round(gapMs / 60_000) });
    }
  }

  return out;
}

/**
 * Fetches a barrister's calendar events covering all provided ISO date strings,
 * then exposes helpers for conflict detection.
 *
 * Pass `null` for barristerId to disable the query (e.g. until a barrister is known).
 */
export function useBarristerBusy(barristerId: string | null, isoDateTimes: string[]) {
  const validDates = isoDateTimes.filter(Boolean);

  const range = validDates.length > 0
    ? (() => {
        const timestamps = validDates.map((d) => new Date(d).getTime());
        const earliest = new Date(Math.min(...timestamps));
        const latest = new Date(Math.max(...timestamps));
        // Expand to full days in local time
        earliest.setHours(0, 0, 0, 0);
        latest.setHours(23, 59, 59, 999);
        return { from: earliest.toISOString(), to: latest.toISOString() };
      })()
    : null;

  // Source the barrister's occupied intervals from the OPAQUE free/busy feed — not a raw event
  // fetch. A clerk cannot read a barrister's schedule by ownerId (the calendar endpoint forces a
  // clerk's ownerId back to their own feed), so the old path silently returned the wrong data and
  // the clash warning never fired for clerks. Free/busy authorises a supported clerk (and self) and
  // returns time-only slots, so clash detection works AND never leaks event detail.
  const q = useQuery({
    queryKey: ['barrister-freebusy', barristerId, range?.from, range?.to],
    queryFn: () =>
      barristerId && range
        ? fetchBarristerFreeBusy(barristerId, range.from, range.to)
        : Promise.resolve([]),
    enabled: Boolean(barristerId) && Boolean(range),
    // Clash detection must see the LATEST schedule every time a scheduling modal opens. With a
    // 60s cache, two overlapping events created in quick succession checked against a stale
    // schedule that didn't yet include the first — so the double-book warning silently never fired.
    // This query only runs when a time is picked (not a hot path), so always-fresh is cheap.
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Project opaque free/busy slots into the internal event shape. Detail fields are intentionally
  // blanked ("Busy", no matter/location) — the clash surfaces only the TIME, never the content.
  const events: RawScheduleEvent[] = useMemo(
    () =>
      (q.data ?? []).map((s, i) => ({
        id: `busy-${i}`,
        startAt: s.start,
        endAt: s.end,
        title: 'Busy',
        eventType: 'busy',
        case: null,
        location: null,
      })),
    [q.data],
  );

  /**
   * Returns all events that overlap with the given time window.
   * Empty array = slot is clear.
   */
  function getConflicts(
    startIso: string,
    durationMinutes: number,
    bufferMinutes: number = BUFFER_MINUTES,
  ): ConflictInfo[] {
    return detectConflicts(events, startIso, durationMinutes, bufferMinutes);
  }

  /** Returns all events on the local calendar date of the given ISO datetime. */
  function getEventsForDate(iso: string): RawScheduleEvent[] {
    if (!iso) return [];
    const dateStr = localDateStr(iso);
    return events.filter((e) => localDateStr(e.startAt) === dateStr);
  }

  return {
    events,
    getConflicts,
    getEventsForDate,
    isLoading: q.isLoading,
  };
}

export interface ProposedSlotClash {
  slot: string; // ISO of the proposed slot
  events: { title: string; startAt: string; endAt: string }[];
}

/**
 * For a set of PROPOSED slots (conference propose / suggest / brief-response), return which slots
 * overlap the target barrister's diary — keyed on the ASSIGNED barrister (event owner), regardless
 * of who is proposing. Warn-only: proposals do not create a diary event, so callers surface a
 * warning and never block. Pass `null` barristerId to disable (e.g. barrister not yet known).
 */
export function useProposedSlotClashes(
  barristerId: string | null,
  isoSlots: string[],
  durationMinutes: number,
): ProposedSlotClash[] {
  const valid = isoSlots.filter(Boolean);
  const { getConflicts } = useBarristerBusy(barristerId, valid);
  const key = valid.join('|');
  return useMemo(() => {
    if (!barristerId || durationMinutes <= 0) return [];
    const out: ProposedSlotClash[] = [];
    for (const iso of valid) {
      const overlaps = getConflicts(iso, durationMinutes).filter((c) => c.kind === 'overlap');
      if (overlaps.length > 0) {
        out.push({
          slot: iso,
          events: overlaps.map((o) => ({ title: o.event.title, startAt: o.event.startAt, endAt: o.event.endAt })),
        });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barristerId, key, durationMinutes, getConflicts]);
}
